<template>
    <div v-if="hasFeature('task_list_scan_widget')" class="rounded-2xl bg-brand-blue px-6 py-5 lg:px-8 lg:py-7 2xl:px-10 2xl:py-9">
        <div class="flex flex-col items-start gap-3 lg:flex-row lg:items-center lg:gap-6 xl:gap-8 2xl:gap-10">
            <!-- Left: Label, heading, button -->
            <div
                class="flex w-full items-center justify-between gap-4 lg:w-60 lg:shrink-0 lg:flex-col lg:items-start lg:justify-start xl:w-auto xl:max-w-sm xl:basis-1/4 2xl:max-w-md"
            >
                <div class="min-w-0 flex-1 lg:w-full lg:flex-none">
                    <p class="!mb-1 text-xs font-semibold tracking-widest text-blue-400 uppercase xl:text-sm">
                        {{ trans('tasks.start_here.title') }}
                    </p>
                    <h2 class="!mb-0 text-lg leading-tight font-bold text-balance text-white lg:text-xl xl:text-2xl 2xl:text-3xl">
                        {{ trans('tasks.start_here.heading') }}
                    </h2>
                </div>
                <div class="flex shrink-0 items-center gap-2 lg:mt-4 lg:w-full xl:mt-5">
                    <jet-button :is-processing="isProcessing" type="button" @click="startScan($event)">
                        <qr-code-icon class="mr-1.5 h-4 w-4" />
                        {{ trans('tasks.start_here.button') }}
                    </jet-button>
                    <!-- Toggle chevron - only visible on tablet/mobile -->
                    <button
                        type="button"
                        :aria-expanded="stepsExpanded"
                        :aria-label="trans('tasks.start_here.toggle_steps')"
                        class="flex h-8 w-8 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/10 lg:hidden"
                        @click="stepsExpanded = !stepsExpanded"
                    >
                        <chevron-down :class="{ 'rotate-180': stepsExpanded }" class="transition-transform" height="14" width="14" />
                    </button>
                </div>
            </div>

            <!-- Vertical divider -->
            <div class="hidden w-px self-stretch bg-white/10 lg:block" />

            <!-- Steps: hidden on tablet unless expanded, always visible on desktop -->
            <div
                :class="stepsExpanded ? 'flex' : 'hidden'"
                class="w-full flex-1 flex-col items-start gap-4 lg:!flex lg:flex-row lg:items-center lg:gap-0"
            >
                <div v-for="(step, index) in steps" :key="index" class="flex flex-1 items-start gap-3 px-0 lg:px-4 xl:gap-4 xl:px-6 2xl:px-8">
                    <div
                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-blue-300 xl:h-11 xl:w-11 2xl:h-12 2xl:w-12"
                    >
                        <icon :icon="step.icon" size="sm" />
                    </div>
                    <div class="leading-tight xl:max-w-xs">
                        <p class="!mb-0 text-xs font-bold tracking-widest text-white/30 uppercase">
                            {{ trans('tasks.start_here.step_label', { number: String(index + 1).padStart(2, '0') }) }}
                        </p>
                        <p class="!mb-0 text-base font-semibold text-white xl:text-lg">{{ step.title }}</p>
                        <p class="!mb-0 text-sm text-pretty text-white/50 xl:text-base">{{ step.description }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { trans } from '@/mixins/i18n';
import { hasFeature } from '@/mixins/hasFeature.js';
import { useCurieIdScanner } from '@/composables/useCurieIdScanner.ts';
import JetButton from '@/Jetstream/Button.vue';
import QrCodeIcon from '../../../icons/QrCode.vue';
import ChevronDown from '../../../icons/ChevronDown.vue';

const emit = defineEmits(['filter-by-asset']);

const { runOpenTaskScanFlow, isProcessing } = useCurieIdScanner();

const stepsExpanded = ref(false);

const steps = [
    {
        title: trans('tasks.start_here.step_1.title'),
        description: trans('tasks.start_here.step_1.description'),
        icon: 'cil-qr-code',
    },
    {
        title: trans('tasks.start_here.step_2.title'),
        description: trans('tasks.start_here.step_2.description'),
        icon: 'cil-airplane-mode',
    },
    {
        title: trans('tasks.start_here.step_3.title'),
        description: trans('tasks.start_here.step_3.description'),
        icon: 'cil-task',
    },
];

const startScan = async (event) => {
    // Drop focus so the button doesn't retain its focus/active styling when
    // the scanner modal closes (e.g. when the user hits Cancel).
    event?.currentTarget?.blur?.();

    const outcome = await runOpenTaskScanFlow();

    if (outcome?.curieIdToFilter) {
        emit('filter-by-asset', outcome.curieIdToFilter);
    }
};
</script>
